import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, Sparkles, LogOut, Lock, User } from 'lucide-react';
import { useAuth } from '../store/auth';
import { homePathForUser, landingPageVisible, usesAdminWorkspace } from '../utils/workspace';
import { useSettings } from '../store/settings';

/**
 * Profile menu — top-right of every layout. Daewoo pattern.
 * Menu renders in a portal so landing-header styles cannot force white text on items.
 */
export default function ProfileDropdown({ variant = 'sales', onDark = false }) {
    const user = useAuth(s => s.user);
    const logout = useAuth(s => s.logout);
    const features = useSettings(s => s.features);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        const onClick = (e) => {
            const t = e.target;
            if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const place = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.right,
                transform: 'translateX(-100%)',
                zIndex: 9999,
            });
        };
        place();
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open]);

    if (!user) return null;

    const isAdminish = usesAdminWorkspace(user);
    const adminHome = homePathForUser(user);
    const canViewLanding = landingPageVisible(user, features);
    const isAdminOrManager = ['admin', 'manager'].includes(user.role);
    const showSwitchView = isAdminish && isAdminOrManager;
    const showLandingInProfile = canViewLanding && !showSwitchView;

    const menu = open && menuStyle && createPortal(
        <div
            ref={menuRef}
            role="menu"
            style={menuStyle}
            className="profile-dropdown-menu w-64 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.18)]"
        >
            <div className="px-4 py-3 border-b border-ink-100 bg-white">
                <div className="font-semibold text-sm text-ink-900 truncate">{user.name}</div>
                <div className="text-xs text-ink-600 truncate">{user.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-brand-teal-700 mt-1 font-bold">{user.role}</div>
            </div>

            {showSwitchView && (
                <div className="py-1.5 border-b border-ink-100 bg-white">
                    <div className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-ink-500 font-bold">Switch view</div>
                    <button type="button" onClick={() => { setOpen(false); nav(adminHome); }}
                        className={`w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-ink-50 ${variant === 'admin' ? '!text-brand-teal-800 !bg-brand-teal-50' : '!text-ink-800'}`}>
                        <LayoutDashboard className="h-4 w-4 shrink-0 text-current" /> Dashboard
                    </button>
                    {canViewLanding && (
                        <button type="button" onClick={() => { setOpen(false); nav('/'); }}
                            className={`w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-ink-50 ${variant === 'sales' ? '!text-brand-teal-800 !bg-brand-teal-50' : '!text-ink-700'}`}>
                            <Sparkles className="h-4 w-4 shrink-0 text-current" /> Landing page
                        </button>
                    )}
                </div>
            )}

            {isAdminish && !isAdminOrManager && (
                <div className="py-1.5 border-b border-ink-100 bg-white">
                    <button type="button" onClick={() => { setOpen(false); nav(adminHome); }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm hover:bg-ink-50 !text-ink-800">
                        <LayoutDashboard className="h-4 w-4 shrink-0 text-current" /> Open admin console
                    </button>
                </div>
            )}

            <div className="py-1.5 border-b border-ink-100 bg-white">
                <Link
                    to={variant === 'admin' ? '/admin/profile' : '/sales/profile'}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 text-sm !text-ink-800 hover:bg-ink-50"
                >
                    <User className="h-4 w-4 shrink-0 text-current" /> My profile
                </Link>
                {showLandingInProfile && (
                    <Link
                        to="/"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm !text-ink-800 hover:bg-ink-50"
                    >
                        <Sparkles className="h-4 w-4 shrink-0 text-current" /> Landing page
                    </Link>
                )}
                {isAdminOrManager && (
                    <Link to="/admin/settings" onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm !text-ink-800 hover:bg-ink-50">
                        <Lock className="h-4 w-4 shrink-0 text-current" /> Settings
                    </Link>
                )}
            </div>

            <button type="button" onClick={() => { setOpen(false); logout(); }}
                className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm !text-brand-red hover:bg-rose-50 bg-white">
                <LogOut className="h-4 w-4 shrink-0 text-current" /> Logout
            </button>
        </div>,
        document.body,
    );

    return (
        <div className="relative" ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                aria-haspopup="menu"
                className={`profile-dropdown-trigger flex items-center gap-2 px-3 py-1.5 rounded-full border transition ${
                    onDark
                        ? 'border-white/80 bg-white/25 text-white backdrop-blur-md hover:bg-white/40 hover:border-white shadow-lg shadow-black/10'
                        : 'border-ink-300 bg-white text-ink-900 hover:border-brand-teal'
                }`}
            >
                <span
                    className={`h-7 w-7 rounded-full grid place-items-center font-bold text-xs shrink-0 ${
                        onDark ? 'bg-brand-teal text-white ring-2 ring-white/60' : 'bg-brand-teal text-white'
                    }`}
                >
                    {user.name?.[0] || 'U'}
                </span>
                <span className={`hidden sm:inline text-sm font-semibold ${onDark ? 'text-white' : 'text-ink-900'}`}>
                    {isAdminish ? 'Admin' : user.name?.split(' ')[0]}
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition ${open ? 'rotate-180' : ''} ${onDark ? 'text-white' : 'text-ink-600'}`} />
            </button>
            {menu}
        </div>
    );
}
